import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import {
  TrendingUp, Package, Truck, IndianRupee, Fuel, Users, AlertCircle, Droplet,
  MessageSquare, UserPlus, ArrowRight, Wallet, ArrowUpRight,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

/**
 * Every KPI has a ``to`` — clicking the tile navigates the user to the page that
 * expands on that number. Receivable → customers list; Payable → vendors list; etc.
 *
 * Tone: ``danger`` styles a KPI in red when it represents money we owe or are owed.
 *
 * ``hideForRoles`` scopes tiles by role. Dispatchers, for example, are the ops
 * traffic-controllers — they should see live delivery counts but NOT financial
 * numbers like revenue, receivable or payable. Product decision (Feb 2026).
 */
const KPIS = [
  { key: "orders_today", label: "Orders today", icon: Package, to: "/app/orders" },
  { key: "orders_active", label: "In transit", icon: Truck, to: "/app/ongoing-trips" },
  { key: "orders_delivered_today", label: "Delivered today", icon: TrendingUp, to: "/app/orders?status=delivered" },
  { key: "litres_today", label: "Litres today", icon: Droplet, suffix: " L", to: "/app/orders" },
  { key: "revenue_today", label: "Revenue today", icon: IndianRupee, prefix: "₹", to: "/app/invoices", hideForRoles: ["dispatcher"] },
  { key: "total_receivable", label: "Receivable (AR)", icon: Wallet, prefix: "₹", tone: "danger", to: "/app/customers?filter=outstanding", hideForRoles: ["dispatcher"] },
  { key: "total_payable", label: "Payable (AP)", icon: AlertCircle, prefix: "₹", tone: "danger", to: "/app/vendors", hideForRoles: ["dispatcher"] },
  { key: "total_customers", label: "Customers", icon: Users, to: "/app/customers" },
  { key: "total_bowsers", label: "Bowsers", icon: Fuel, to: "/app/fleet" },
];

const fmt = (n, prefix = "", suffix = "") => {
  if (n === undefined || n === null) return "—";
  const val = typeof n === "number" ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : n;
  return `${prefix}${val}${suffix}`;
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const role = user?.role || "";
  // Dispatchers see delivery ops only — no revenue / AR / AP tiles or lines.
  const visibleKpis = KPIS.filter((k) => !(k.hideForRoles || []).includes(role));
  const showFinance = role !== "dispatcher";
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/reports/dashboard").then((r) => r.data),
    refetchInterval: 30000,
  });
  const { data: recent } = useQuery({
    queryKey: ["dashboard-recent"],
    queryFn: () => api.get("/orders?limit=8").then((r) => r.data.items),
  });

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Operations · Live</h1>
          <p className="text-sm text-muted-foreground">Realtime snapshot across all depots and bowsers.</p>
        </div>
        <Link to="/app/dispatch"><Button className="pill-btn" data-testid="btn-open-dispatch">Open dispatch board →</Button></Link>
      </div>

      {/* KPI grid — every tile links to the page that expands the number */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {visibleKpis.map((k) => {
          const value = isLoading ? "—" : fmt(data?.[k.key], k.prefix, k.suffix || "");
          const isDanger = k.tone === "danger" && (data?.[k.key] || 0) > 0;
          return (
            <Link
              key={k.key}
              to={k.to}
              className={`kpi-card group cursor-pointer transition-all hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md ${isDanger ? "border-red-300 bg-red-50/40" : ""}`}
              data-testid={`kpi-${k.key}`}
            >
              <div className="flex items-start justify-between gap-2 text-muted-foreground">
                <div className="min-w-0 text-xs font-semibold uppercase tracking-widest">{k.label}</div>
                <k.icon size={16} className={`mt-0.5 shrink-0 ${isDanger ? "text-red-600" : ""}`} />
              </div>
              <div
                className={`mt-3 truncate font-display text-xl font-black sm:text-2xl md:text-3xl ${isDanger ? "text-red-700" : ""}`}
                title={String(value)}
              >
                {value}
              </div>
              <div className="mt-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-primary/0 transition-colors group-hover:text-primary">
                Open <ArrowUpRight size={10} />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Orders (last 7 days)</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.series || []}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                <XAxis dataKey="date" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                <YAxis fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Line type="monotone" dataKey="orders" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
                {showFinance && (
                  <Line type="monotone" dataKey="revenue" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ r: 3 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Status breakdown</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.status_breakdown || []}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                <XAxis dataKey="status" fontSize={11} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Complaints + Leads snapshot */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card data-testid="dash-complaints-card">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare size={16} className="text-red-500" /> Open complaints &amp; tickets
              </CardTitle>
              <div className="mt-1 text-xs text-muted-foreground">
                <b className="text-red-600">{data?.open_complaints || 0} open</b>
                {" · "}
                <b className="text-orange-600">{data?.urgent_complaints || 0} urgent</b>
                {" · "}
                <b>{data?.complaint_tickets || 0} complaints</b>
              </div>
            </div>
            <Link to="/app/support"><Button variant="outline" size="sm" data-testid="btn-view-tickets">View all <ArrowRight size={12} className="ml-1" /></Button></Link>
          </CardHeader>
          <CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Subject</th>
                  <th className="px-4 py-2">From</th>
                  <th className="px-4 py-2">Priority</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_complaints || []).map((t) => (
                  <tr key={t.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 font-mono">
                      <Link to="/app/support" className="hover:text-primary">{t.code}</Link>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <div className="font-semibold">{t.subject}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.category}</div>
                    </td>
                    <td className="px-4 py-2 text-xs">{t.created_by_name}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        t.priority === "urgent" ? "bg-red-100 text-red-700" :
                        t.priority === "high" ? "bg-orange-100 text-orange-700" :
                        t.priority === "medium" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-700"
                      }`}>
                        {t.priority || "medium"}
                      </span>
                    </td>
                  </tr>
                ))}
                {(!data?.recent_complaints || data.recent_complaints.length === 0) && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">All quiet — no open tickets 🎉</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card data-testid="dash-leads-card">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <UserPlus size={16} className="text-primary" /> New enquiries &amp; leads
              </CardTitle>
              <div className="mt-1 text-xs text-muted-foreground">
                <b className="text-primary">{data?.new_leads || 0} new</b>
                {" · "}
                <b className="text-emerald-600">{data?.hot_leads || 0} hot</b>
                {" · from web form + outbound"}
              </div>
            </div>
            <Link to="/app/leads"><Button variant="outline" size="sm" data-testid="btn-view-leads">View pipeline <ArrowRight size={12} className="ml-1" /></Button></Link>
          </CardHeader>
          <CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Company / Name</th>
                  <th className="px-4 py-2 text-right">Volume</th>
                  <th className="px-4 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_leads || []).map((l) => (
                  <tr key={l.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 font-mono">
                      <Link to="/app/leads" className="hover:text-primary">{l.code}</Link>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <div className="font-semibold">{l.company || l.name}</div>
                      <div className="text-muted-foreground">{l.phone}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs">
                      {l.expected_monthly_litres > 0 ? `${l.expected_monthly_litres.toLocaleString("en-IN")} L/mo` : "—"}
                    </td>
                    <td className="px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">{l.source?.replace("_", " ")}</td>
                  </tr>
                ))}
                {(!data?.recent_leads || data.recent_leads.length === 0) && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No new enquiries yet — share your landing page!</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Recent orders</CardTitle>
          <Link to="/app/orders"><Button variant="outline" size="sm" data-testid="btn-view-orders">View all</Button></Link>
        </CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3 text-right">Qty (L)</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Bowser · Driver</th>
              </tr>
            </thead>
            <tbody>
              {(recent || []).map((o) => (
                <tr key={o.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-3 font-mono font-semibold">
                    <Link to={`/app/orders/${o.id}`} className="hover:text-primary" data-testid={`recent-order-${o.code}`}>{o.code}</Link>
                  </td>
                  <td className="px-4 py-3">{o.customer_name}</td>
                  <td className="px-4 py-3 text-right font-mono">{o.quantity_litres}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{o.total_amount?.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {o.bowser_code ? `${o.bowser_code} · ${o.driver_name}` : "—"}
                  </td>
                </tr>
              ))}
              {(!recent || recent.length === 0) && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
